const ScrollRestorer = (() => {
  const STORAGE_PREFIX = 'scroll:';
  let ticking = false;

  const defaults = {
    animate: true,
    maxAnimateDistance: 1500,
    throttle: true,
    clearAfterRestore: false,
    retryInterval: 50,
    retryTimeout: 3000
  };

  let options = { ...defaults };

  const getPageKey = () =>
    location.pathname + location.search;

  const getStorageKey = () =>
    STORAGE_PREFIX + getPageKey();

  const saveScroll = () => {
    sessionStorage.setItem(
      getStorageKey(),
      String(window.scrollY)
    );
  };

  const throttledSave = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        saveScroll();
        ticking = false;
      });
      ticking = true;
    }
  };

  /**
   * Attempt to restore scroll, retrying until the page is tall enough.
   * Safari often fires restore events before layout is complete,
   * so the target position may be unreachable on the first attempt.
   */
  const restoreScroll = (animated = false) => {
    const key = getStorageKey();
    const stored = sessionStorage.getItem(key);
    if (stored === null) return;

    const target = Number(stored);
    if (target === 0) return;

    const attempt = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= target) {
        applyScroll(target, animated);
        return true;
      }
      return false;
    };

    // Try immediately — works in most Chrome/Firefox cases
    if (attempt()) return;

    // Retry loop for Safari bfcache / late-layout scenarios
    const start = Date.now();
    const interval = setInterval(() => {
      if (attempt() || Date.now() - start > options.retryTimeout) {
        // If we timed out, scroll as far as we can
        if (Date.now() - start > options.retryTimeout) {
          applyScroll(target, false);
        }
        clearInterval(interval);
      }
    }, options.retryInterval);
  };

  const applyScroll = (target, animated) => {
    const distance = Math.abs(window.scrollY - target);

    if (
      animated &&
      options.animate &&
      distance < options.maxAnimateDistance
    ) {
      window.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      window.scrollTo(0, target);
    }

    if (options.clearAfterRestore) {
      sessionStorage.removeItem(getStorageKey());
    }
  };

  const init = (userOptions = {}) => {
    options = { ...defaults, ...userOptions };

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Save on scroll (throttled)
    window.addEventListener('scroll', () => {
      options.throttle ? throttledSave() : saveScroll();
    });

    // Save right before the page enters bfcache (Safari) or unloads
    window.addEventListener('pagehide', () => {
      saveScroll();
    });

    // popstate — back/forward navigation (non-bfcache)
    window.addEventListener('popstate', () => {
      restoreScroll(true);
    });

    // pageshow — handles both initial load AND bfcache restore (Safari)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was restored from bfcache — restore scroll position
        restoreScroll(true);
      }
    });

    // DOMContentLoaded — initial page load (non-bfcache)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        restoreScroll(false);
      });
    } else {
      // Script loaded after DOMContentLoaded already fired
      restoreScroll(false);
    }
  };

  return { init };
})();

ScrollRestorer.init({
  animate: true,
  maxAnimateDistance: 1200
});
